<template>
	<div>
		<div>
			<FullCalendar
				ref="fullCalendar"
				:options="calendarOptions"
			/>
		</div>
		<div class="mt-3 d-flex flex-wrap">
			<div
				v-for="(item, index) of indicator"
				:key="index"
				class="d-flex align-items-center mr-4"
			>
				<b-badge
					class="badge p-3 mr-2"
					:style="{
						backgroundColor: item.color,
						display: 'inline-block',
					}"
				></b-badge>
				<span class="h6 mt-2">
					{{ item.name }}
				</span>
			</div>
		</div>
	</div>
</template>
<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import { filtersHelper } from '@/utils';

export default {
    props: {
        eventsData: {
            type: Array,
            required: false,
        },
		initialView: {
			type: String,
			default: 'timeGridDay',
		},
		calendarModes: {
            type: String,
            required: false,
		},
		calendarInitialDate: { // eg. format: 2025-11-30
			type: String,
			default: null,
		},
		indicator: {
			type: Array,
			required: false,
		},
    },
	components: { FullCalendar },
	data() {
		return {
			calendarOptions: {
				plugins: [ dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin ],
				initialView: this.initialView,
				initialDate: this.calendarInitialDate,								// initial date if calendar is in timeGridDay (eg. format: 2025-11-30)
				headerToolbar: {													// calendar's header
					left: 'prev,next today',
					center: 'title',												// default showing current calendar's range
					right: this.headerRightOptions(),
					// right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',	// (eg. dayGridMonth,timeGridWeek,timeGridDay,listMonth )
				},
				timeZone: 'local',													// set time zone
				allDaySlot: false,													// enable/disable slot for entire day
				editable: true,														// enable drag & drop (move events)
				droppable: true,													// allow dragging and dropping events
				nowIndicator: false, 												// show current time indicator
				locale: 'en',														// calendar language
				firstDay: 0,														// starting day in week view (0 indicates Sunday)
				dayMaxEvents: false,												// limit max no. of events per day, can set to integer												
				height: 'auto',														// set height of calendar (eg. 600 is 600px)
				weekNumbers: false,													// to display week number
				hiddenDays: false,													// hide specific days of the week (eg. [0, 6])
				weekends: true,														// visibility of weekends (Saturday & Sunday)
				dragRevertDuration: 500,											// duration for revert animation if event is dragged but not dropped
				displayEventTime: false,											// display start & end time in event card
				displayEventEnd: false,												// display event end time in event card (only if displayEventTime: true)
				validRange: {  },													// restrict viewable range (eg. { start: '2025-10-01', end: '2025-12-31' })
				slotMinTime: '08:00:00',											// available start time
				slotMaxTime: '19:00:00',											// available end time
				slotDuration: '00:30:00',											// duration of each time slot in timeGrid view
				slotLabelInterval: '00:30',											// sets inteval between the slot labels
				slotLabelFormat: {													// cuztomize format of time labels on the left side of timeGrid
					hour: '2-digit',
					minute: '2-digit',
					omitZeroMinute: false, 											// to display minutes when the hour is on its hour
				},
				eventColor: '#ffffff', 												// event card bullet point color
				eventDisplay: 'block', 												// event card display type (eg. block, list-item, none) 
				eventTextColor: '#ffffff',											// event card wording color
				eventBackgroundColor: '#7C4DFF',									// event card background color
				eventClassNames: () => {											// add custom class to style event card
					return ['custom-fc-list-event-dot'];
				},							
				// eventOverlap: true,												// events can overlap with another event at the same time
				eventOverlap: (still, moving) => {						// events can overlap with another event at the same time
					const stillEvent = still.extendedProps?.id; // existing event
					const movingEvent = moving.extendedProps?.id; // moving event
					return stillEvent !== movingEvent; // Prevent overlap if the id is the same
				},
				slotEventOverlap: false,											// avoid events displaying overlaps/crossover
				eventDurationEditable: true,										// the duration of events can be resized (dragging), if editable: true
				eventTimeFormat: {
					hour: '2-digit',
					minute: '2-digit',
					second: false,
					meridiem: false,
				},
				eventConstraint: {													// events only can be moved within this time period
					startTime: '08:00:00',
					endTime: '19:00:00',
				},
				eventDrop: this.update,												// triggered when event is moved (dragged)
				eventResize: this.update,											// triggered when event is changed by resizing it (dragging the edges)
				eventClick: this.leftClick,											// triggered when an event is left clicked
				selectable: true,													// enable click & drag to click on a specific time period on calendar
				select: this.create,												// triggered when user clicked on a time slot (if selectable: true)
				selectAllow: (selectInfo) => {
					return selectInfo.start.getDate() === selectInfo.end.getDate(); // Only allow selection within the same day
				},
				datesSet: this.filter,												// triggered when datetime filter changed
				eventDidMount: (info) => {											// add custom event listener when events are clicked eg. right click event
					info.el.addEventListener('contextmenu', (e) => {				// custom event - right click
						e.preventDefault();
						this.rightClick(info);
					});
				},
				eventMouseEnter: function(info) {									// triggered when mouse enters an event
					info;
				},
				eventMouseLeave: function(info) {									// triggered when mouse leaves an event
					info;
				},
				// events: [														// sample event data
				// 	{	
				// 		id: '123',
				// 		title: 'Briefing with team',
				// 		start: '2025-11-10 11:00:00',
				// 		end: '2025-11-10 11:00:00',
				// 		extendedProps: {},

				// 		backgroundColor: '#ff5733',   // event background
				// 		borderColor: '#ff5733',       // border
				// 		textColor: '#ffffff',         // text color
				// 	},
				// ],
			},
		};
	},
	methods: {
		filter(info) {
			this.setActionPermission(info);
			this.$emit("filter", this.normalizeEventData({data: info, type: 'filter'}));
		},
		create(info) {
			// if(this.checkOverlap(info)) return; // disable event create if slot occupied
			this.$emit("create", this.normalizeEventData({data: info, type: 'create'}));
		},
        update(info) {
			this.$emit("update", this.normalizeEventData({data: info, type: 'update'}));
		},
		leftClick(info) {
			this.$emit("leftClick", this.normalizeEventData({data: info, type: 'update'}));
		},
		rightClick(info) {
			this.$emit("rightClick", this.normalizeEventData({data: info, type: 'update'}));
		},
		setActionPermission(info) { // enable/disable interactions (click on event) depending on the active view
			let access = {
				editable: true,
				selectable: true,
				eventClick: true,
				eventDidMount: true,
			};

				switch(info.view.type) {
					case 'timeGridDay':				// 'Day' view
						access = {
							editable: true,
							selectable: true,
							eventClick: true,
							eventDidMount: true,
						};
						break;
					case 'timeGridWeek':			// 'Week' view
						access = {
							editable: true,
							selectable: true,
							eventClick: true,
							eventDidMount: true,
						};
						break;
					case 'dayGridMonth':			// 'Month' view
						access = {
							editable: false,
							selectable: true,
							eventClick: true,
							eventDidMount: true,
						};
						break;
					case 'listMonth':				// 'List' view
						access = {
							editable: false,
							selectable: false,
							eventClick: false,
							eventDidMount: false,
						};
						break;
					default:
						access = {
							editable: true,
							selectable: true,
							eventClick: true,
							eventDidMount: true,
						};
				}
			
			const calendarApi = this.$refs.fullCalendar?.getApi();
			if(calendarApi) {
				// Update interaction-related options
				calendarApi.setOption('editable', access.editable); // drag & drop
				calendarApi.setOption('selectable', access.selectable);
				calendarApi.setOption('eventClick', access.eventClick ? this.leftClick : null);
				calendarApi.setOption('eventDidMount', access.eventDidMount ? (info) => {
					info.el.addEventListener('contextmenu', (e) => {
						e.preventDefault();
						this.rightClick(info);
					})} : null);
			}
		},
		// checkOverlap(info) {
		// 	const calendarApi = this.$refs.fullCalendar?.getApi();
		// 	return calendarApi?.getEvents().some(e =>
		// 		info.start < e.end && info.end > e.start
		// 	) || false;
		// },
		normalizeEventData({data, type}) {

			let id, start, end, title = '';
			let details = {};
			let view = data?.view?.type;

			switch(type) {
				case 'filter':
					start = filtersHelper.getDateFormat(data.startStr, true);
					end = filtersHelper.getDateFormat(data.endStr, true);
					break;

				case 'create':
					start = filtersHelper.getDateTimeNumeric({value: data.startStr, timeIsShort: true});
					end = filtersHelper.getDateTimeNumeric({value: data.endStr, timeIsShort: true});
					break;

				case 'update':
					id = data.event._def.publicId;
					title = data.event._def.title || '';
					details = data.event._def.extendedProps;
					start = filtersHelper.getDateTimeFormat(data.event._instance.range.start, true, true);
					end = filtersHelper.getDateTimeFormat(data.event._instance.range.end, true, true);
					break;

				default:
			}

			return {
				id,
				title,
				details,
				start,
				end,
				view,
			};
		},
		refreshCalendarEvents() { // re-render calendar whenever this.eventsData is updated
			const calendarApi = this.$refs.fullCalendar?.getApi();
			if(calendarApi) {
				calendarApi.removeAllEvents([]); // reset events
				calendarApi.addEventSource(this.eventsData); // mount events
			}
		},
		headerRightOptions() {
			switch(this.calendarModes) {
				case "standard":
					return "dayGridMonth,timeGridWeek,timeGridDay,listMonth";
				case "weekAndDay":
					return "timeGridWeek,timeGridDay";
				default:
					return "dayGridMonth,timeGridWeek,timeGridDay,listMonth";
			}
		},
	},
	watch: { // re-render calendar whenever this.eventsData is updated
		eventsData: {
			handler() {
				this.refreshCalendarEvents();
			},
			immediate: true,
		},
	},
}
</script>
<style>
/* ========== GENERAL CALENDAR STYLING ========== */
.fc {
	font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
	color: #333;
	background-color: #fff;
	border-radius: 10px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	overflow: hidden;
}

/* Calendar header toolbar */
.fc-toolbar-title {
	font-size: 1.25rem !important;
	font-weight: 600 !important;
	color: #222 !important;
}

/* Theme accent — matches the app's primary purple ($purple / #7C4DFF).
   Everything below reads these instead of hardcoding a colour, so changing the
   theme is a one-line edit and every calendar in the app follows. */
.fc {
	--app-accent: #7C4DFF;
	--app-accent-hover: #6B3FE8;
	--app-accent-active: #5E2FE0;
	--app-accent-soft: rgba(124, 77, 255, 0.12);
}

/* ========== VIEW SWITCHER BUTTONS ========== */
/* Force all view buttons to same width */
.fc-toolbar-chunk .fc-button {
    display: inline-flex;          /* allow flex alignment */
    justify-content: center;       /* center text horizontally */
    align-items: center;           /* center text vertically */
    width: 80px;                   /* fixed width for all buttons */
    min-width: 80px;               /* ensure smallest buttons don’t shrink */
    padding: 6px 0 !important;     /* top/bottom padding */
    border-radius: 8px !important;
    font-size: 0.875rem !important;
    font-weight: 500;
    margin: 0 4px;                 /* spacing between buttons */
    border: none;
    background-color: #f5f6f8;
    color: #333;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    transition: all 0.2s ease;
}

/* Hover state */
.fc-toolbar-chunk .fc-button:hover {
    background-color: var(--app-accent-soft);
    color: var(--app-accent-active);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0,0,0,0.12);
}

/* Active / selected view button */
.fc-toolbar-chunk .fc-button.fc-button-active {
    background-color: var(--app-accent);
    color: #fff;
    box-shadow: 0 3px 6px rgba(0,0,0,0.15);
}

/* Remove focus outline */
.fc-toolbar-chunk .fc-button:focus {
    outline: none;
}

.fc-button-primary {
	background-color: var(--app-accent) !important;
	color: #fff !important;
	border: none !important;
}
.fc-button-primary:hover {
	background-color: var(--app-accent-hover) !important;
}
.fc-button-active {
	background-color: var(--app-accent-active) !important;
}
.fc-button-primary:disabled {
	background-color: #c9b8ff !important;
	border: none !important;
}
.fc-button-primary:focus {
	box-shadow: 0 0 0 0.2rem rgba(124, 77, 255, 0.25) !important;
}

/* ========== TIME SLOT LABELS STYLING ========== */
.fc-timegrid-slot-label-cushion {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 6px;
    font-size: 0.7rem;              /* smaller, modern font */
    font-weight: 400;
    color: #555;
    background-color: #f5f6f8;       /* soft gray background for label */
    border-radius: 4px;              /* rounded pill style */
    height: 22px;                     /* consistent label height */
    margin: 2px 0;                    /* vertical spacing */
    box-shadow: inset 0 0 2px rgba(0,0,0,0.05); /* subtle inner shadow */
    transition: background 0.15s ease, color 0.15s ease;
}

/* Hover effect for labels when dragging or selecting */
.fc-timegrid-slot-label-cushion:hover {
    background-color: var(--app-accent-soft);
    color: var(--app-accent-active);
}

/* Align the time labels with the slot rows */
.fc-timegrid-slot-label {
    padding: 0;
    margin: 0;
    height: auto; 
}

/* ========== EVENT CARD STYLING ========== */
.fc-event {
	border: none !important;
	border-radius: 6px !important;
	padding: 4px 8px !important;
	font-size: 0.875rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.fc-event:hover {
	transform: scale(1.01);
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

/* ========== LIST VIEW EVENTS ========== */
.fc .fc-list-event-title {
    background-color: var(--app-accent) !important;
    color: #fff !important;
}

.fc-list-event-graphic {
    background-color: var(--app-accent) !important;
    color: #fff !important;
}

/* ========== SCROLL AREA ========== */
.fc-scroller-harness {
	scrollbar-width: thin;
	scrollbar-color: #ccc transparent;
}
.fc-scroller-harness::-webkit-scrollbar {
	width: 6px;
}
.fc-scroller-harness::-webkit-scrollbar-thumb {
	background-color: #bbb;
	border-radius: 3px;
}

/* ========== TODAY HIGHLIGHT ========== */
.fc-day-today {
	background-color: rgba(124, 77, 255, 0.07) !important;
}

/* ========== DAY HEADERS (Sun, Mon, Tue, etc.) ========== */
.fc-col-header-cell {
    background-color: #f8f9fa;        /* soft light background */
    color: #333;                       /* dark text for readability */
    font-weight: 600;                  /* bold labels */
    font-size: 0.875rem;               /* slightly smaller font */
    text-align: center;                /* center day names */
    border-bottom: 1px solid #e0e0e0; /* subtle bottom border */
    padding: 8px 0;
    position: relative;
}

/* Optional: show the date below day name */
.fc-col-header-cell .fc-col-header-cell-cushion {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 0.75rem;                 /* smaller font for date */
    color: #666;
}

/* Hover effect when dragging or selecting */
.fc-col-header-cell:hover {
    background-color: #eaf5ff;
}

/* Highlight today column */
.fc-col-header-cell.fc-day-today {
    background-color: var(--app-accent-soft);
    border-bottom: 2px solid var(--app-accent);
    color: var(--app-accent-active);
}

/* Optional: rounded top corners for first/last day in week view */
.fc-col-header-cell:first-child {
    border-top-left-radius: 10px;
}
.fc-col-header-cell:last-child {
    border-top-right-radius: 10px;
}


</style>